/**
 * Printing over Bluetooth Low Energy, straight from the browser (Chrome on
 * Android, Web Bluetooth). No app to install, no print dialog: pair once,
 * then every receipt is one tap.
 *
 * Thermal printers do not agree on one BLE service, so the common ones are
 * asked for and the first characteristic that accepts writes is used.
 * Classic-Bluetooth-only printers (SPP) cannot be reached from a browser;
 * for those the ordinary print dialog stays available.
 */

// Minimal typings: Web Bluetooth is not in TypeScript's DOM library.
interface Char {
  properties: { write: boolean; writeWithoutResponse: boolean }
  writeValue?: (v: BufferSource) => Promise<void>
  writeValueWithoutResponse?: (v: BufferSource) => Promise<void>
  writeValueWithResponse?: (v: BufferSource) => Promise<void>
}
interface Service { getCharacteristics: () => Promise<Char[]> }
interface Server { connected: boolean; connect: () => Promise<Server>
                   getPrimaryServices: () => Promise<Service[]>; disconnect: () => void }
export interface BtDevice { id: string; name?: string; gatt?: Server }
interface Bluetooth {
  requestDevice: (o: unknown) => Promise<BtDevice>
  getDevices?: () => Promise<BtDevice[]>
}

/** Services thermal printers commonly expose. */
export const PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb',      // most Chinese 58/80 mm printers
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',      // Xprinter, Rongta and kin
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',      // Microchip ISSC transparent UART
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb',
]

function bt(): Bluetooth | null {
  return (typeof navigator !== 'undefined'
    && (navigator as unknown as { bluetooth?: Bluetooth }).bluetooth) || null
}

export function bluetoothAvailable(): boolean { return bt() !== null }

let current: { device: BtDevice; char: Char } | null = null

/** Ask the salesman to pick the printer (needs a tap: browsers allow the
 *  picker only from one). */
export async function pairPrinter(): Promise<BtDevice> {
  const b = bt()
  if (!b) throw new Error('This browser cannot use Bluetooth. Use Chrome on Android.')
  const device = await b.requestDevice({ acceptAllDevices: true,
                                         optionalServices: PRINTER_SERVICES })
  current = { device, char: await writable(device) }
  return device
}

/** The printer paired before, if the browser still allows it. */
async function remembered(id: string): Promise<BtDevice | null> {
  const b = bt()
  if (!b?.getDevices) return null
  try {
    return (await b.getDevices()).find((d) => d.id === id) ?? null
  } catch {
    return null
  }
}

async function writable(device: BtDevice): Promise<Char> {
  if (!device.gatt) throw new Error('That device cannot be connected to')
  const server = device.gatt.connected ? device.gatt : await device.gatt.connect()
  for (const svc of await server.getPrimaryServices()) {
    for (const ch of await svc.getCharacteristics()) {
      if (ch.properties.writeWithoutResponse || ch.properties.write) return ch
    }
  }
  throw new Error('This device does not take print jobs over Bluetooth LE')
}

async function connect(id: string | null): Promise<{ device: BtDevice; char: Char }> {
  if (current && current.device.gatt?.connected && (!id || current.device.id === id)) return current
  const device = current?.device ?? (id ? await remembered(id) : null)
  if (!device) throw new Error('PAIR')
  current = { device, char: await writable(device) }
  return current
}

/** Send the bytes in small pieces: BLE packets are small, and a printer
 *  that is sent too much too fast drops the rest of the receipt. */
export async function send(bytes: Uint8Array, printerId: string | null, chunk = 180): Promise<void> {
  const { char } = await connect(printerId)
  const fast = char.properties.writeWithoutResponse && char.writeValueWithoutResponse
  for (let i = 0; i < bytes.length; i += chunk) {
    const part = bytes.slice(i, i + chunk)
    if (fast) await char.writeValueWithoutResponse!(part)
    else if (char.writeValueWithResponse) await char.writeValueWithResponse(part)
    else await char.writeValue!(part)
    if (fast) await new Promise((r) => setTimeout(r, 12))
  }
}
